import React, { useRef } from 'react';
import Button from '../../../../components/Button';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import FoundingCreatorIcon from '../../../../assets/founding_creator_icon.png';
import AvatarDefault from '../../../../assets/medium_default_optimized.png';
import EditIcon from '../../../../assets/edit-green-icon.svg';
import BadgeWithTooltip from '../../../../components/BadgeWithTooltip';
import { BADGE_INFO } from '../../../../constants/badges';
import SocialLinks from '../SocialLinks';
import { useNavigate } from 'react-router-dom';
import Stat from '../Stat';
import useTruncatedElement from 'src/hooks/useTruncatedElement';
import RankBadge from '../../../../components/RankBadge';
import PointsCard from '../PointsCard';
import { WriterBadgeInfo } from '../../../../components/WriterBadgeInfo';
import useRankBadgeTooltip from 'src/hooks/useRankBadgeTooltip';
import { useAtom } from 'jotai';
import { profileAtom } from 'src/components/Settings/atom';
import { UserDTO } from '@twygs/shared/publisher-interface';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

interface ProfileHeaderProps {
  user: UserDTO;
  isOwner: boolean;
  onFollow: () => void;
  isLoadingFollow: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  isOwner,
  onFollow,
  isLoadingFollow
}) => {
  const navigate = useNavigate();
  const {
    id,
    imgUrl,
    firstName,
    username,
    isVerifiedAuthor,
    description,
    followerNumber,
    followingNumber,
    contributionNumber,
    socialLinks,
    currentRank,
    sentReputation,
    receivedReputation
  } = user;
  const {
    openPopup,
    totalAwardedPoints,
    isTouchDevice,
    handleMouseEnter,
    handleClick,
    handleMouseLeave
  } = useRankBadgeTooltip();

  const readMoreRef1 = useRef<HTMLDivElement>(null);
  const [profile] = useAtom(profileAtom);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const truncatedText1 = useTruncatedElement({
    ref: readMoreRef1
  });

  return (
    <div className="mb-4 md:mb-[30px] max-w-[604px] mx-auto">
      <div className="flex gap-5 mb-4 md:mb-6">
        <img
          src={imgUrl || AvatarDefault}
          className="rounded-full md:rounded-[16px] h-[80px] w-[80px] md:h-[188px] md:w-[188px] object-cover flex-shrink-0"
          alt=""
        />
        <div className="flex flex-col w-full gap-2 md:gap-3 min-w-0">
          <div className="flex gap-2 items-start flex-wrap">
            <div className="flex flex-wrap gap-1 md:gap-3 items-center min-w-0">
              <h1
                title={firstName}
                className="text-base md:text-2xl font-[800] truncate max-w-full"
              >
                {firstName}
              </h1>
              {isVerifiedAuthor && (
                <BadgeWithTooltip
                  icon={VerifiedIcon}
                  alt="Verified Author"
                  title={BADGE_INFO.VERIFIED_AUTHOR.title}
                  description={BADGE_INFO.VERIFIED_AUTHOR.description}
                  className="w-6 h-6 md:w-8 md:h-8"
                />
              )}
              {user.isFoundingCreator && (
                <BadgeWithTooltip
                  icon={FoundingCreatorIcon}
                  alt="Founding Creator"
                  title={BADGE_INFO.FOUNDING_CREATOR.title}
                  description={BADGE_INFO.FOUNDING_CREATOR.description}
                  className="w-6 h-6 md:w-8 md:h-8"
                />
              )}
              {id === profile.id && (
                <div className="px-2 py-1 order-4 md:order-3 rounded-[80px] bg-growth-green-50 text-sm text-growth-green-900 font-[600]">
                  You
                </div>
              )}
            </div>
            <div className="flex gap-1 md:gap-3 items-center">
              {username && (
                <span className="font-[600] text-xs md:text-lg text-02-dim-gray">@{username}</span>
              )}
              <div
                className="relative"
                onMouseEnter={() => !isTouchDevice && handleMouseEnter(id)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick(id);
                }}
                onMouseLeave={() => !isTouchDevice && handleMouseLeave()}
              >
                <RankBadge
                  textSize="text-xs md:text-sm"
                  iconSize="w-4 h-4 md:w-5 md:h-5"
                  rank={currentRank}
                />
                {openPopup && (
                  <div className="absolute top-[34px] md:top-[-20px] left-[-50%] md:left-[120px] z-10">
                    <WriterBadgeInfo
                      rank={currentRank}
                      totalReceivedPoint={totalAwardedPoints.totalReceivedPoint}
                      totalReceivedUsers={totalAwardedPoints.totalReceivedUsers}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex gap-[10px] md:gap-4">
              <Stat
                className="text-sm"
                label={followerNumber === 1 ? 'follower' : 'followers'}
                value={followerNumber}
              />
              <Stat className="text-sm" label="following" value={followingNumber} />
              <Stat
                className="text-sm"
                label={contributionNumber === 1 ? 'article' : 'articles'}
                value={contributionNumber}
              />
            </div>
            <PointsCard
              earnedPoints={receivedReputation?.totalPoints ?? 0}
              givenPoints={sentReputation?.totalPoints ?? 0}
              readers={receivedReputation?.totalUsers ?? 0}
              writers={sentReputation?.totalUsers ?? 0}
            />
            {!isOwner && (
              <div className="flex gap-[10px]">
                <Button
                  variant="primary"
                  onClick={() => {
                    if (!userId) return UnauthorizedDispatcher();
                    onFollow();
                  }}
                  disabled={isLoadingFollow}
                  isLoading={isLoadingFollow}
                  className="!px-[14px] !py-2"
                >
                  {user.isFollowing ? 'Following' : 'Follow'}
                </Button>

                <SocialLinks links={socialLinks} />
              </div>
            )}
          </div>
          {isOwner && (
            <Button
              variant="secondary"
              onClick={() => navigate('/article/manage?tab=profile')}
              className="hidden md:flex px-[14px] !py-2 border w-fit"
            >
              Edit profile
              <img src={EditIcon} alt="edit" className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        {description && <h2 className="text-base md:text-lg font-[700] md:font-[800]">About me</h2>}
        {isOwner && (
          <Button
            variant="secondary"
            onClick={() => navigate('/article/manage?tab=profile')}
            className="flex md:hidden px-[14px] !py-2 border w-fit ml-auto"
          >
            Edit profile
            <img src={EditIcon} alt="edit" className="ml-2 w-4 h-4" />
          </Button>
        )}
      </div>
      {description && (
        <div>
          <p
            ref={readMoreRef1}
            className={`whitespace-pre-wrap mt-2 text-xs md:text-sm font-[600] md:font-[400] ${!truncatedText1.isReadingMore && 'line-clamp-[6]'}`}
          >
            {description}
          </p>
          {truncatedText1.isTruncated && (
            <div onClick={truncatedText1.toggleIsShowingMore} className="cursor-pointer">
              <p className="text-sm font-[900] text-growth-green-500 cursor-pointer hover:opacity-80 mt-[8px]">
                {truncatedText1.isReadingMore ? 'Show less' : 'Show more'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;
